import flow from "lodash/fp/flow";
import pick from "lodash/fp/pick";
import map from "lodash/fp/map";
import find from "lodash/fp/find";
import cloneDeep from "lodash/fp/cloneDeep";
import assign from "lodash/fp/assign";
import findIndex from "lodash/fp/findIndex";
import round from "lodash/fp/round";
import isNil from "lodash/fp/isNil";
import moment from "moment";
import constants from "../../core/constants";
import {
  toObject,
  multiplyFloatingPointByCurrency,
  getTime,
} from "../../core/utils";
class RoutePageTransform {
  constructor($injector, Lists) {
    "ngInject";
    this.Reservation = $injector.get("Reservation");
    this.Lists = Lists.all;
  }
  justify(data) {
    const formatPrice = (item) => {
      item.price /= constants.currencyBy;

      return item;
    };
    const formatDistance = (item) => {
      item.totalDistance = `${round(item.total_distance / 1000)}KM`;

      return item;
    };

    return toObject(flow([formatPrice, formatDistance])(data));
  }

  justifySchedule(data, timezone = null) {
    const weekDays = cloneDeep(this.Lists.weekDays);
    const format = (item) => {
      item = assign({ _id: item._id }, item._source);
      const getLabel = (l) => l.name === item.label;

      if (!isNil(timezone)) {
        // Start Time
        const localTimezone =
          new Date().getTimezoneOffset() * constants.fromMinToMilli;

        if (timezone > 0) {
          item.scheduleTime = moment(item.time - timezone).format(
            constants.timeFormat
          );
          item.time -= timezone;
          // Predicted End Time
          item.predicted_end_time = moment()
            .startOf("day")
            .add(item.predicted_end_time - timezone - localTimezone)
            .format(constants.timeFormat);
        } else {
          item.scheduleTime = moment(item.time + Math.abs(timezone)).format(
            constants.timeFormat
          );
          item.time += Math.abs(timezone);
          item.time = moment()
            .startOf("day")
            .add(item.time - localTimezone, "milliseconds")
            .format("hh:mm A");
          // Predicted End Time
          item.predicted_end_time = moment()
            .startOf("day")
            .add(item.predicted_end_time + Math.abs(timezone) - localTimezone)
            .format(constants.timeFormat);
        }

        // Estimates from response are the difference between scheduleStartTime and arrival/departure value
        // so need to plus scheduleStartTime to arrival/departure to display their values correctly
        if (item.manual_estimates) {
          item.manual_estimates = item.manual_estimates.map((element) => {
            element.arrival += item.time;
            element.departure += item.time;

            return toObject(element, true);
          });
        }
      }

      item.scheduleWeekDays = weekDays.map((day) => {
        const clonedDay = Object.assign({}, day);
        const isSelectedDay = (x) => clonedDay.value === x;

        clonedDay.isSelected = findIndex(isSelectedDay)(item.weekdays) > -1;

        return clonedDay;
      });
      item.label = Object.assign({}, find(getLabel)(this.Lists.scheduleLabels));
      item.captain_price = isNil(item.captain_price)
        ? null
        : item.captain_price / constants.currencyBy;
      item.arrival_time_allowance /= constants.fromMinToMilli;
      item.departure_time_allowance /= constants.fromMinToMilli;
      item.must_book_on_the_fly = item.ride_booking_method === "on_the_fly";

      // If there is manual estimates on schedule set is_manual_estimates_eta with true
      item.is_manual_eta = !isNil(item.manual_estimates);
      item.isManualEstimatesEditable = false;
      item.name = moment(item.time).clone().format(constants.timeFormat);
      if (item.pricing_parameters) {
        item.pricing_parameters.base_fare /= constants.currencyBy;
        item.pricing_parameters.distance_bucket_fare /= constants.currencyBy;
        item.pricing_parameters.time_bucket_fare /= constants.currencyBy;
        item.customized_pricing = {
          key: "customized_pricing",
          value: true,
          isDisabled: true,
        };
      }
      if (!item.pricing_parameters && item.city.ride_categories) {
        const selectedCategoryDetails = item.city.ride_categories.find(
          (categoryItem) => categoryItem.category === item.ride_category._id
        );
        this.cityCategoryPricing = selectedCategoryDetails.features.find(
          (feature) => feature.name === "pricing_version"
        );
        if (
          !isNil(this.cityCategoryPricing) &&
          this.cityCategoryPricing.value === "dynamic"
        ) {
          item.pricingVersionValue = "dynamic";
          item.pricing_parameters = {};
          item.pricing_parameters = { ...this.cityCategoryPricing.config };
          item.pricing_parameters.base_fare /= constants.currencyBy;
          item.pricing_parameters.maximum_fare /= constants.currencyBy;
          item.pricing_parameters.time_bucket_fare /= constants.currencyBy;
          item.pricing_parameters.distance_bucket_fare /= constants.currencyBy;
        } else item.pricingVersionValue = "fixed";
        item.customized_pricing = {
          key: "customized_pricing",
          value: false,
          isDisabled: true,
        };
      }

      item.route.uniqueName = `${item.route.name} - ${item.route._id}`;
      return item;
    };
    return map(flow([format, toObject]))(data);
  }

  // eslint-disable-next-line complexity
  reverseJustifySchedule(item) {
    const option = {};
    const localTimezone =
      new Date().getTimezoneOffset() * constants.fromMinToMilli * -1;
    // Difference between local timezone and route city timezone
    const diffTimezone = Math.abs(this.route.city.timezone - localTimezone);
    // Get Selected Time with respect of timezone
    const valueWithDiffTimezone =
      localTimezone > this.route.city.timezone
        ? item.time - diffTimezone
        : item.time + diffTimezone;

    /* Local timezone and route city timezone are the same, will send selected value
    // Local timezone and route city timezone aren't the same, will respect local timezone
    // handle if difference timezone is negative so need to add 24 hours
    */
    option.time =
      this.route.city.timezone === localTimezone ||
      Math.abs(this.route.city.timezone) === localTimezone
        ? item.time
        : valueWithDiffTimezone < 0
        ? valueWithDiffTimezone + constants.fromDayToMilli
        : valueWithDiffTimezone < constants.fromDayToMilli
        ? valueWithDiffTimezone
        : valueWithDiffTimezone - constants.fromDayToMilli;

    option._id = item._id;
    if (
      item.pricing_parameters &&
      item.new_schedule_customized_pricing_params
    ) {
      const pickedPricing = { ...item.pricing_parameters };
      option.pricing_parameters = pick(
        [
          "base_fare",
          "distance_bucket_size",
          "distance_bucket_fare",
          "base_distance",
          "time_bucket_size",
          "time_bucket_fare",
          "base_time",
        ],
        pickedPricing
      );

      option.pricing_parameters.base_fare = multiplyFloatingPointByCurrency(
        item.pricing_parameters.base_fare
      );
      option.pricing_parameters.distance_bucket_fare = multiplyFloatingPointByCurrency(
        item.pricing_parameters.distance_bucket_fare
      );
      option.pricing_parameters.time_bucket_fare = multiplyFloatingPointByCurrency(
        item.pricing_parameters.time_bucket_fare
      );
    }
    if ("captain" in item) {
      option.captain = item.captain.name
        ? item.captain.name._id
        : item.captain._id
        ? item.captain._id
        : null;
    } else {
      option.captain = null;
    }

    if ("bus_type" in item) {
      option.bus_type = item.bus_type._id
        ? item.bus_type._id
        : item.bus_type.name
        ? item.bus_type.name._id
        : null;
    }

    if ("ride_category" in item) {
      option.ride_category = item.ride_category._id;
    }

    if ("label" in item) {
      option.label = item.label.name;
    }

    if ("captain_price" in item) {
      option.captain_price = item.captain_price.value * 100;
    }

    if ("active" in item) {
      option.active = item.active.value;
    }

    if ("arrival_time_allowance" in item) {
      option.arrival_time_allowance =
        item.arrival_time_allowance.value * constants.fromMinToMilli;
    }

    if ("departure_time_allowance" in item) {
      option.departure_time_allowance =
        item.departure_time_allowance.value * constants.fromMinToMilli;
    }

    if ("must_book_on_the_fly" in item) {
      option.ride_booking_method =
        isNil(item.must_book_on_the_fly.value) ||
        item.must_book_on_the_fly.value === false
          ? "regular"
          : "on_the_fly";
    }

    if ("ride_booking_method" in item && !("must_book_on_the_fly" in item)) {
      option.ride_booking_method = item.ride_booking_method.value
        ? "on_the_fly"
        : "regular";
    }

    if ("schedule_buffer" in item) {
      option.schedule_buffer = item.schedule_buffer.value;
    }

    if ("corporate" in item) {
      option.corporate = item.corporate.name
        ? item.corporate.name._id
        : item.corporate._id
        ? item.corporate._id
        : null;
    } else {
      option.corporate = null;
    }

    if ("ride_price" in item) {
      option.ride_price = item.ride_price.value * constants.currencyBy;
    }

    if ("supply_cost" in item) {
      option.supply_cost = item.supply_cost.value * constants.currencyBy;
    }

    if ("manual_estimates" in item) {
      // We need to send the difference between scheduleStartTime and arrival/departure
      // To overcome if schdeule overlap between PM and AM format
      const scheduleStartTime = item.time;
      const getTimeDiff = (time) => {
        if (getTime(time) < scheduleStartTime) {
          return constants.fromDayToMilli - scheduleStartTime + getTime(time);
        }
        return getTime(time) - scheduleStartTime;
      };
      option.manual_estimates = item.manual_estimates.map(
        ({ arrival, departure }, index) => ({
          arrival: index ? getTimeDiff(arrival.value) : 0,
          departure: getTimeDiff(departure.value),
        })
      );
    }

    option.is_list_allowed = item.is_list_allowed;
    option.is_qr_code_allowed = item.is_qr_code_allowed;

    return option;
  }
}

export default RoutePageTransform;



// WEBPACK FOOTER //
// ./src/pages/route/route.transforms.js